import React, { useState } from "react";
import { X, ChevronRight, Info } from "lucide-react";
import { toast } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader";
import KeplrIcon from "../images/Keplr_icon_ver.1.3_2.svg"

export const WalletConnectModal = ({ isOpen, onClose, onWalletConnected }) => {
  const [loading, setLoading] = useState(false);
  const [loadingWallet, setLoadingWallet] = useState(null);

  const handleConnect = async (wallet) => {
    try {
      setLoadingWallet(wallet);
      setLoading(true);

      if (wallet === "metamask") {
        // Check if MetaMask is installed
        if (typeof window.ethereum !== 'undefined') {
          // Request account access
          const accounts = await window.ethereum.request({
            method: 'eth_requestAccounts'
          });
          
          if (accounts.length > 0) {
            const address = accounts[0];
            toast.success(`Connected to MetaMask: ${address.slice(0, 6)}...${address.slice(-4)}`);
            onWalletConnected(address);
            onClose();
            return address;
          }
        } else {
          toast.error('MetaMask is not installed. Please install MetaMask to connect your wallet.');
        }
      } else if (wallet === "keplr") {
        if (!window.keplr) {
          toast.error("Keplr wallet is not installed");
          return;
        }

        // For Keplr, we'll use a simple connection
        const chainId = "cataclysm-1"; // Nibiru Mainnet
        await window.keplr.enable(chainId);
        const offlineSigner = window.keplr.getOfflineSigner(chainId);
        const accounts = await offlineSigner.getAccounts();

        if (accounts.length > 0) {
          const address = accounts[0].address;
          toast.success(`Connected to Keplr: ${address.slice(0, 6)}...${address.slice(-4)}`);
          onWalletConnected(address);
          onClose();
          return address;
        }
      } else if (wallet === "leap") {
        if (!window.leap) {
          toast.error("Leap wallet is not installed");
          return;
        }

        // For Leap, we'll use a simple connection
        const chainId = "cataclysm-1"; // Nibiru Mainnet
        await window.leap.enable(chainId);
        const offlineSigner = window.leap.getOfflineSigner(chainId);
        const accounts = await offlineSigner.getAccounts();

        if (accounts.length > 0) {
          const address = accounts[0].address;
          toast.success(`Connected to Leap: ${address.slice(0, 6)}...${address.slice(-4)}`);
          onWalletConnected(address);
          onClose();
          return address;
        }
      }
    } catch (error) {
      console.error(`Error connecting to ${wallet}:`, error);
      toast.error(`Cannot connect to your ${wallet} wallet: ${error.message}`);
    } finally {
      setLoading(false);
      setLoadingWallet(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="w-[95vw] sm:w-[90vw] md:w-[480px] max-w-[480px] min-w-[280px] rounded-[12px] sm:rounded-[16px] bg-white shadow-xl border-0 p-0 z-50 max-h-[90vh] overflow-y-auto mb-4 sm:mb-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
          </button>
          
          <div className="text-center pt-6 sm:pt-8 pb-4 sm:pb-6 px-4 sm:px-6">
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Connect Your Wallet</h2>
            <p className="text-gray-600 text-xs sm:text-sm px-2">Choose your preferred wallet to connect</p>
          </div>

          <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-4">
            {/* Wallet Options */}
            <div className="grid grid-cols-3 gap-3">
              {/* MetaMask */}
              <button
                onClick={() => handleConnect("metamask")}
                disabled={loading}
                className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 border border-gray-200 hover:border-purple-300"
              >
                {loadingWallet === "metamask" ? (
                  <ClipLoader size={32} color="#6366f1" />
                ) : (
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg"
                    alt="MetaMask"
                    className="w-8 h-8 mb-2"
                  />
                )}
                <span className="text-xs text-gray-700 font-medium">MetaMask</span>
              </button>

              {/* Keplr */}
              <button
                onClick={() => handleConnect("keplr")}
                disabled={loading}
                className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 border border-gray-200 hover:border-purple-300"
              >
                {loadingWallet === "keplr" ? (
                  <ClipLoader size={32} color="#6366f1" />
                ) : (
                                          <img src={KeplrIcon || "/placeholder.svg"} alt="Keplr" className="w-8 h-8 mb-2" />

                )}
                <span className="text-xs text-gray-700 font-medium">Keplr</span>
              </button>

              {/* Leap */}
              <button
                onClick={() => handleConnect("leap")}
                disabled={loading}
                className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 border border-gray-200 hover:border-purple-300"
              >
                {loadingWallet === "leap" ? (
                  <ClipLoader size={32} color="#6366f1" />
                ) : (
                  <img
                    src="https://assets.leapwallet.io/logos/leap-cosmos-logo.svg"
                    alt="Leap"
                    className="w-8 h-8 mb-2"
                  />
                )}
                <span className="text-xs text-gray-700 font-medium">Leap</span>
              </button>
            </div>

            <div className="flex items-center justify-center pt-3 sm:pt-4">
              <button className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600 hover:text-gray-800 transition-colors">
                <Info className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Learn more about wallet options</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
